import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  Directive,
  ElementRef,
  EnvironmentInjector,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

/**
 * Shows a Material circular spinner centered over each Form.io content image while it downloads
 * (large guide images can take seconds), and removes it once the image loads. Form.io renders the
 * `<img>` into its own DOM, so we observe the host, wrap each pending image and overlay a real
 * MatProgressSpinner instance on top of it.
 */
@Directive({
  selector: '[scwImageLoader]',
  standalone: true,
})
export class ImageLoaderDirective implements OnInit, OnDestroy {
  private static readonly IMAGE_SELECTOR =
    '.formio-component-content img:not([data-scw-loader]), .formio-component-htmlelement img:not([data-scw-loader])';

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly appRef = inject(ApplicationRef);

  private readonly spinners = new Map<HTMLImageElement, ComponentRef<MatProgressSpinner>>();
  private observer?: MutationObserver;

  public ngOnInit(): void {
    this.processImages();
    this.observer = new MutationObserver(() => this.processImages());
    this.observer.observe(this.host.nativeElement, { childList: true, subtree: true });
  }

  public ngOnDestroy(): void {
    this.observer?.disconnect();
    this.spinners.forEach((spinner) => spinner.destroy());
    this.spinners.clear();
  }

  private processImages(): void {
    // Drop spinners whose image was removed by a Form.io re-render before it finished loading.
    this.spinners.forEach((spinner, image) => {
      if (!image.isConnected) {
        spinner.destroy();
        this.spinners.delete(image);
      }
    });

    this.host.nativeElement
      .querySelectorAll<HTMLImageElement>(ImageLoaderDirective.IMAGE_SELECTOR)
      .forEach((image) => this.attachSpinner(image));
  }

  private attachSpinner(image: HTMLImageElement): void {
    image.dataset['scwLoader'] = '1';

    if (image.complete && image.naturalWidth > 0) {
      return;
    }

    const wrapper = document.createElement('span');
    wrapper.className = 'scw-img-loading';
    image.parentNode?.insertBefore(wrapper, image);
    wrapper.appendChild(image);

    const spinner = createComponent(MatProgressSpinner, { environmentInjector: this.environmentInjector });
    spinner.setInput('mode', 'indeterminate');
    spinner.setInput('diameter', 48);
    spinner.setInput('strokeWidth', 4);
    this.appRef.attachView(spinner.hostView);

    const spinnerElement = spinner.location.nativeElement as HTMLElement;
    spinnerElement.classList.add('scw-img-loading__spinner');
    wrapper.appendChild(spinnerElement);
    this.spinners.set(image, spinner);

    const finish = (): void => {
      spinner.destroy();
      this.spinners.delete(image);
      wrapper.classList.remove('scw-img-loading');
    };

    image.addEventListener('load', finish, { once: true });
    image.addEventListener('error', finish, { once: true });
  }
}
